import { driver as createDriver, type Driver, type DriverHook, type DriveStep } from 'driver.js';
import { afterHighlight, beforeHighlight, destroyDriver, renderHighlight } from './hooks';
import { renderPopover } from './popover';
import { getStepElement, getStepElementSelector, hideStage, removeStage } from './stage';
import type { TourStep } from './types';

const completeTour = (driver: Driver) => {
  if (!driver.onComplete) {
    destroyDriver(driver);
    return;
  }

  const activeIndex = driver.isDrivingThroughChildren ? driver.lastRootIndex : driver.getActiveIndex();

  driver.onComplete(activeIndex ?? 0, false);

  destroyDriver(driver);
};

export const moveToRoot = (driver: Driver) => {
  const newSteps = driver.rootSteps.map((s) => {
    return {
      ...s,
      element: getStepElementSelector(s),
      disableActiveInteraction: !s.interaction,
    } satisfies DriveStep;
  });

  driver.setSteps(newSteps);
  driver.isDrivingThroughChildren = false;
  removeStage();

  const rootNextStep = findRootNextStep(driver);
  const rootNextStepIndex = rootNextStep ? (driver.rootSteps ?? []).indexOf(rootNextStep) : -1;

  if (!rootNextStep || rootNextStepIndex === -1) {
    if (driver.onComplete) {
      completeTour(driver);
    }

    destroyDriver(driver);
    return;
  }

  driver.moveTo(rootNextStepIndex);
};

export const clickNext: DriverHook = (_, step, { driver }) => {
  const activeStep = step as TourStep;

  if (driver.isLastStep() && !activeStep.children && !driver.isDrivingThroughChildren) {
    if (driver.onComplete) {
      completeTour(driver);
    }

    destroyDriver(driver);
    return;
  }

  if (driver.isLastStep() && driver.isDrivingThroughChildren) {
    moveToRoot(driver);
    return;
  }

  const nextStep = findNextStep(driver);

  if (nextStep?.children && !getStepElement(nextStep)) {
    const nextStepIndex = (driver.rootSteps ?? []).indexOf(nextStep);
    const reachableNextStep = findRootNextStep(driver, nextStepIndex);

    if (!reachableNextStep) {
      destroyDriver(driver);
      return;
    }

    driver.moveTo(driver.rootSteps.indexOf(reachableNextStep));
    return;
  }

  if (activeStep.interaction !== 'click') {
    driver.moveNext();
    return;
  }

  if (getStepElement(nextStep)) {
    driver.moveNext();
    return;
  }

  if (!getStepElement(nextStep)) {
    hideStage();
  }

  const intervalId = window.setInterval(() => {
    if (getStepElement(nextStep)) {
      // wait for page transition, if any
      window.setTimeout(driver.moveNext, 200);
      clearInterval(intervalId);
    }
  }, 300);

  driver.addInterval(intervalId);
};

export const clickPrevious: DriverHook = (_, __, { driver, config }) => {
  const previousStep = findPreviousStep(driver);
  const previousStepIndex = !previousStep ? -1 : ((config.steps ?? []) as TourStep[]).indexOf(previousStep);

  if (previousStepIndex !== -1) {
    driver.moveTo(previousStepIndex);
  }
};

export const findNextStep = (driver: Driver): TourStep | null => {
  const config = driver.getConfig();
  const steps = (config.steps ?? []) as TourStep[];

  const activeStepIndex = driver.getActiveIndex() ?? -1;

  if (activeStepIndex === -1) return null;

  const activeStep = steps[activeStepIndex];
  const nextStep = steps[activeStepIndex + 1];

  if (!activeStep || !nextStep) return null;

  return nextStep;
};

export const findRootNextStep = (driver: Driver, index?: number): TourStep | null => {
  const lastStep = driver.rootSteps[driver.lastRootIndex];
  const nextStepIndex = index ?? driver.lastRootIndex + 1;
  const nextStep = driver.rootSteps[nextStepIndex];

  if (!lastStep || !nextStep) return null;

  if (!getStepElement(nextStep)) {
    return findRootNextStep(driver, nextStepIndex + 1);
  }

  return nextStep;
};

export const findPreviousStep = (driver: Driver): TourStep | null => {
  const config = driver.getConfig();
  const steps = (config.steps ?? []) as TourStep[];

  const activeStepIndex = driver.getActiveIndex() ?? -1;

  if (activeStepIndex === -1) return null;

  const activeStep = steps[activeStepIndex];
  const previousStep = steps[activeStepIndex - 1];

  if (!activeStep || !previousStep) return null;

  return previousStep;
};

export const isNextDisabled = (driver: Driver) => {
  const activeStep = driver.getActiveStep() as TourStep;

  if (!activeStep) return false;

  if (
    (activeStep.interaction === 'click' && !activeStep.interactionTarget) ||
    (activeStep.interactionTarget && !document.querySelector(activeStep.interactionTarget))
  ) {
    return true;
  }

  if (driver.isLastStep()) return false;

  const nextStep = findNextStep(driver);
  if (!nextStep) return true;

  if (!driver.isDrivingThroughChildren && nextStep.children) {
    return !findRootNextStep(driver);
  }

  return !getStepElement(nextStep);
};

export const isPreviousDisabled = (driver: Driver) => {
  if (driver.isFirstStep()) return true;

  const activeStep = driver.getActiveStep() as TourStep;
  if (!activeStep) return false;

  const previousStep = findPreviousStep(driver);
  if (!previousStep) return true;

  return !getStepElement(previousStep);
};

export const driver = createDriver({
  allowClose: false,
  overlayColor: 'black',
  overlayOpacity: 0.4,
  allowKeyboardControl: false,
  onPopoverRender: renderPopover,
  onNextClick: clickNext,
  onPrevClick: clickPrevious,
  onHighlightStarted: beforeHighlight,
  onHighlighted: renderHighlight,
  onDeselected: afterHighlight,
  onDestroyStarted: (_, __, { driver }) => destroyDriver(driver),
  onDestroyed: (_, __, { driver }) => destroyDriver(driver),
});
